import moment from 'moment'
import 'moment/locale/ro'
import {
  IconDefinition,
  faBagShopping,
  faBellConcierge,
  faBoxOpen,
  faCommentSms,
  faCreditCard,
  faFileInvoice,
  faMobileScreen,
  faPen,
  faPowerOff,
  faPrint,
  faQrcode,
  faSliders,
  faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons'
import { MAX_FREE_RETOUCHES, MenuProduct, User } from '@meniudigital/shared'
import { NavigateFunction } from 'react-router-dom'
import { Screen } from 'src/screens'

// What "Du-mă acolo" gets to work with
type GoToContext = {
  navigate: NavigateFunction
  user: User
  products: MenuProduct[]
}

export type WhatsNewFeature = {
  id: string
  // The day the feature reached production, YYYY-MM-DD
  launchedAt: string
  title: string
  description: string
  icon: IconDefinition
  accent: [string, string]
  imageSrc: string
  // The screenshot's pixel size, [width, height]. The modal reserves its box before it loads, so the lazy
  // loading knows how far down the list each screenshot is
  imageSize: [number, number]
  imageAlt: string
  // With a before image, the modal wipes from it to imageSrc instead of showing one screenshot
  beforeImageSrc?: string
  // "Du-mă acolo": opens the place where the feature lives
  goTo?: (context: GoToContext) => void
  // "Află mai multe": a button under the image that opens the feature's own explainer
  learnMore?: (context: GoToContext) => void
}

// Newest first, the same list for every venue. The dashboard card takes its date from the first one, the modal shows them all
export const WHATS_NEW_FEATURES: WhatsNewFeature[] = [
  {
    id: 'default-menu-section',
    launchedAt: '2026-10-14',
    title: 'Alegerea secțiunii implicite a meniului',
    description:
      'Dacă meniul tău are mai multe secțiuni (Restaurant, Bar, Vinuri), acum poți alege care dintre ele se deschide la scanarea codului QR. Clienții pot trece oricând la celelalte. O găsești în Setări, fila Avansate, la Meniuri / Secțiuni Multiple.',
    icon: faQrcode,
    accent: ['#4fe3c1', '#0fa38a'],
    imageSrc: '/whats-new/default-menu-section.jpg',
    imageSize: [964, 319],
    imageAlt: 'Alegerea secțiunii deschise la scanarea codului QR, în Setări',
    goTo: () => window.openSettingsModal('advanced'),
  },
  {
    id: 'change-menu-name',
    launchedAt: '2026-10-14',
    title: 'Schimbarea numelui restaurantului',
    description:
      'Acum poți schimba singur numele restaurantului sau al meniului, din Setări, fila De bază. Username-ul, linkul meniului și codurile QR tipărite rămân aceleași.',
    icon: faPen,
    accent: ['#ffb347', '#ff7a1a'],
    imageSrc: '/whats-new/change-menu-name.jpg',
    imageSize: [964, 731],
    imageAlt: 'Fereastra de schimbare a numelui restaurantului',
    goTo: () => window.openSettingsModal('basic'),
  },
  {
    id: 'invoice-numbers',
    launchedAt: '2026-10-06',
    title: 'Numărul facturii la fiecare plată',
    description:
      'În Plăți efectuate, fiecare plată arată acum numărul facturii proforme și al facturii fiscale, ca să le poți găsi ușor în contabilitate. Ajungi acolo din Abonamentul meu.',
    icon: faFileInvoice,
    accent: ['#b79cff', '#7c3aed'],
    imageSrc: '/whats-new/invoice-numbers.jpg',
    imageSize: [848, 462],
    imageAlt: 'O plată cu numărul facturii proforme și al facturii fiscale',
    goTo: ({ navigate }) => navigate(Screen.BusinessOrders),
  },
  {
    id: 'photo-retouching',
    launchedAt: '2026-09-22',
    title: 'Retușarea automată a pozelor',
    description: `Acum, fiecare poză de produs pe care o încarci este retușată automat: lumină, contrast și culori mai bune. Poți reveni oricând la poza originală din fereastra produsului. În varianta de test ai ${MAX_FREE_RETOUCHES} retușări gratuite.`,
    icon: faWandMagicSparkles,
    accent: ['#ff8ad4', '#c026d3'],
    imageSrc: '/whats-new/photo-retouching-after.jpg',
    imageSize: [964, 723],
    beforeImageSrc: '/whats-new/photo-retouching-before.jpg',
    imageAlt: 'Aceeași poză de produs, înainte și după retușare',
    // A product whose photo is already retouched, where the "Cu retușare" pill compares it with the original;
    // else any product with its own photo, else any product (its window is where a photo gets uploaded, and retouched)
    goTo: ({ products }) => {
      const hasOwnPhoto = (product: MenuProduct) =>
        !!product.imageKey && !product.imageInfo?.isGeneric && !product.imageKey.includes('generic')
      const target = products.find(product => product.retouchedImageUrl) || products.find(hasOwnPhoto) || products[0]
      if (target) {
        window.openProductModal(target)
      }
    },
  },
  {
    id: 'web-menu-redesign',
    launchedAt: '2026-09-16',
    title: 'Meniu web mai modern',
    description:
      'Meniul văzut de clienții tăi arată acum mai proaspăt și mai aerisit: butoane cu efect de sticlă, un selector de categorii cu poze, căutarea mereu la îndemână și revenirea sus dintr-o atingere. Se aplică automat, fără să schimbi nimic.',
    icon: faMobileScreen,
    accent: ['#ff7b8a', '#e0245e'],
    imageSrc: '/whats-new/web-menu.jpg',
    imageSize: [964, 699],
    imageAlt: 'Meniul web pe telefon: lista de produse și noul selector de categorii',
    goTo: ({ user }) => {
      window.open(`${process.env.REACT_APP_MENU_BASE_URL}/${user.username}`, '_blank', 'noopener,noreferrer')
    },
  },
  {
    id: 'pos-printing',
    launchedAt: '2026-08-16',
    title: 'Printarea comenzilor pe POS',
    description:
      'Dacă primești comenzi online, acum poți instala aplicația MeniuDigital pe un POS Sunmi cu imprimantă: vezi comenzile în timp real și tipărești bonul de comandă cu o atingere pe Printează.',
    icon: faPrint,
    // The green of the POS printing modal (greenGradient)
    accent: ['#87c544', '#489127'],
    imageSrc: '/whats-new/pos-printing.jpg',
    imageSize: [964, 648],
    imageAlt: 'O comandă pe ecranul POS-ului Sunmi și bonul tipărit',
    learnMore: () => window.openPosPrintingModal(),
  },
  {
    id: 'order-sms',
    launchedAt: '2026-07-09',
    title: 'Confirmarea comenzii prin SMS',
    description:
      'La comenzile cu livrare sau ridicare, clientul primește acum un SMS când confirmi sau respingi comanda. Dacă și-a lăsat adresa de email, primește și un email cu detaliile comenzii.',
    icon: faCommentSms,
    accent: ['#ffd166', '#f2a20c'],
    imageSrc: '/whats-new/order-sms.jpg',
    imageSize: [964, 699],
    imageAlt: 'SMS-ul și emailul primite de client când comanda e confirmată',
  },
  {
    id: 'digitalization-package',
    launchedAt: '2026-06-14',
    title: 'Pachetul de Digitalizare',
    description:
      'Acum ne poți trimite meniul și îl configurăm noi: produsele, pozele și, dacă vrei, declarația nutrițională. Vezi prețul pe loc, în funcție de câte produse ai, iar meniul e gata în 4-5 zile lucrătoare (sau în circa 2 zile, la urgență). Odată cu pachetul, îți configurăm gratuit și profilul, cu logo, orar și locație, dacă ne trimiți detaliile.',
    icon: faBoxOpen,
    accent: ['#93a5ff', '#4f46e5'],
    imageSrc: '/whats-new/digitalization-package.jpg',
    imageSize: [964, 785],
    imageAlt: 'Fereastra Digitalizare Meniu, cu estimarea de preț',
    learnMore: () => window.openOnboardingPackageModal(),
  },
  {
    id: 'online-ordering',
    launchedAt: '2026-05-14',
    title: 'Comenzile online, direct din meniu',
    description:
      'Acum clienții tăi pot comanda direct din meniul digital, cu livrare sau ridicare, ca alternativă la comenzile telefonice sau la un site propriu. Poți activa comenzile online din secțiunea Comenzi, unde găsești și pașii de configurare. Comenzile sunt incluse în abonament, fără comision din partea noastră.',
    icon: faBellConcierge,
    // The orange of the online ordering modal (orangeGradient)
    accent: ['#f2a14b', '#ff7a50'],
    imageSrc: '/whats-new/online-ordering.jpg',
    imageSize: [964, 699],
    imageAlt: 'Comanda în meniul web și detaliile clientului, cu livrare',
    goTo: ({ navigate }) => navigate(Screen.ConsumerOrders),
    learnMore: () => window.openOnlineOrderingInfoModal(),
  },
  {
    id: 'settings-redesign',
    launchedAt: '2026-02-02',
    title: 'Reorganizarea setărilor',
    description:
      'Setările sunt acum împărțite în trei file (De bază, Generale și Avansate), iar fiecare opțiune are cardul ei, cu iconiță și o scurtă explicație. Așa poți găsi mai ușor ce cauți, de la WiFi și programul de lucru până la widgetul de comenzi online.',
    icon: faSliders,
    accent: ['#4fe3c1', '#0fa38a'],
    imageSrc: '/whats-new/settings-redesign.jpg',
    imageSize: [964, 908],
    imageAlt: 'Setările, cu filele De bază, Generale și Avansate',
    goTo: () => window.openSettingsModal('general'),
  },
  {
    id: 'recurring-payment',
    launchedAt: '2025-12-04',
    title: 'Plata recurentă a abonamentului',
    description:
      'Când plătești abonamentul online, cu cardul, poți activa acum plata recurentă: abonamentul se plătește automat înaintea expirării, fără să-l mai reînnoiești manual. O poți dezactiva oricând, din Abonamentul meu.',
    icon: faCreditCard,
    accent: ['#b79cff', '#7c3aed'],
    imageSrc: '/whats-new/recurring-payment.jpg',
    imageSize: [964, 805],
    imageAlt: 'Abonamentul meu, cu plata recurentă activă',
    goTo: ({ navigate }) => navigate(Screen.MySubscription),
  },
  {
    id: 'order-widget',
    launchedAt: '2025-10-23',
    title: 'Widgetul de comenzi online',
    description:
      'Acum poți afișa în partea de jos a meniului butoane rapide către site-ul tău, telefon, Glovo, Bolt Food sau Wolt, ca clienții să comande rapid. Poți marca și o reducere pentru comenzile directe. Îl activezi din Setări, fila Generale.',
    icon: faBagShopping,
    accent: ['#ff7b8a', '#e0245e'],
    imageSrc: '/whats-new/order-widget.jpg',
    imageSize: [964, 249],
    imageAlt: 'Previzualizarea widgetului de comenzi, cu butoanele și reducerea',
    goTo: () => window.openSettingsModal('general'),
  },
  {
    id: 'menu-availability',
    launchedAt: '2025-06-30',
    title: 'Dezactivarea temporară a meniului',
    description:
      'Acum poți opri temporar meniul, de exemplu cât timp localul e închis pentru renovare: linkul și codurile QR nu îl mai deschid până când îl pornești din nou. O găsești în Setări, fila Avansate, la Disponibilitate Meniu.',
    icon: faPowerOff,
    accent: ['#87c544', '#489127'],
    imageSrc: '/whats-new/menu-availability.jpg',
    imageSize: [964, 199],
    imageAlt: 'Setarea Disponibilitate Meniu, cu meniul accesibil',
    goTo: () => window.openSettingsModal('advanced'),
  },
]

// Calendar days since the launch: the launch day is 0 ("azi"), the day after is 1 ("ieri"), whatever the hour
export const getDaysSinceLaunch = (launchedAt: string) => {
  return Math.max(moment().startOf('day').diff(moment(launchedAt, 'YYYY-MM-DD').startOf('day'), 'days'), 0)
}

// The id of the newest feature the owner has opened the modal on. Everything listed above it is unread, so a new
// launch shows up as unread on its own. Kept per browser
const SEEN_STORAGE_KEY = 'whatsNewSeenFeatureId'
// With nothing stored (a first visit, or a browser that logged out, which clears localStorage) or an id no longer
// listed, only the launches of the last month count as unread, not the whole history
const UNREAD_WINDOW_DAYS = 30

export const getUnreadCount = (features: WhatsNewFeature[]) => {
  const seenIndex = features.findIndex(feature => feature.id === localStorage.getItem(SEEN_STORAGE_KEY))
  if (seenIndex === -1) {
    return features.filter(feature => getDaysSinceLaunch(feature.launchedAt) <= UNREAD_WINDOW_DAYS).length
  }
  return seenIndex
}

export const markAllSeen = (features: WhatsNewFeature[]) => {
  localStorage.setItem(SEEN_STORAGE_KEY, features[0].id)
}

// "azi", "ieri", "acum 3 zile", "acum 20 de zile", "acum o lună"
export const formatDaysAgo = (days: number) => {
  if (days === 0) {
    return 'azi'
  }
  if (days === 1) {
    return 'ieri'
  }
  return `acum ${moment.duration(days, 'days').locale('ro').humanize()}`
}

export const formatLaunchAge = (launchedAt: string) => formatDaysAgo(getDaysSinceLaunch(launchedAt))
